import { ChartOptions, ColorType, DeepPartial, PriceFormatCustom } from "lightweight-charts";
import { PayoffChartDisplayOptions } from "../components/payoffGraph/payoffChart/payoffChart.typings";
import { formatLargeNumber } from "./screener";
import { Theme } from "@mui/material";

/** Fixed offset for crosshair legend after the left (OI) price scale */
export const PAYOFF_LEGEND_LEFT_SCALE_OFFSET_PX = 70

/** Right P/L scale: full Indian grouping, no K/M abbreviation, zero decimals */
export const formatPayoffPnlScalePrice = (price: number): string => {
    if (!Number.isFinite(price)) return '-'
    return Math.round(price).toLocaleString('en-IN', {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0
    })
}

/** Left OI scale: K/M/B abbreviations, no trailing .0 before suffix (e.g. 10M not 10.0M) */
export const formatPayoffOiScalePrice = (price: number): string => {
    if (!Number.isFinite(price)) return '-'
    const formatted = formatLargeNumber(price)
    return formatted.replace(/(\d+)\.0+([KMBT])$/i, '$1$2')
}

export const payoffPnlPriceFormat: PriceFormatCustom = {
    type: 'custom',
    formatter: formatPayoffPnlScalePrice,
    minMove: 1
}

export const payoffOiPriceFormat: PriceFormatCustom = {
    type: 'custom',
    formatter: formatPayoffOiScalePrice,
    minMove: 1
}

export const getDefaultLayoutOptions = (theme: Theme, strikes: Set<number>, showTimescale = true, showPnLScale = true, showOiScale = true, isEmbedded = false): DeepPartial<ChartOptions> => {
    return {
        localization: {
            timeFormatter: (time: any) => {
                return time.toString()
            }
        },
        handleScale: isEmbedded ? false : {
            axisPressedMouseMove: {
                price: true
            }
        },
        handleScroll: isEmbedded ? false : {
            pressedMouseMove: true,
            pressedVerticalMouseMove: false
        },
        timeScale: {
            tickMarkFormatter: (time: any) => {
                if (strikes.has(time as number)) return time.toString()
                return ''
            },
            borderColor: theme.palette.divider,
            fixLeftEdge: true,
            borderVisible: false,
            fixRightEdge: true,
            visible: showTimescale,
            lockVisibleTimeRangeOnResize: isEmbedded
        },
        layout: {
            background: {
                type: ColorType.Solid,
                color: theme.palette.background.paper
            },
            fontFamily: `'Work Sans', sans-serif`,
            textColor: theme.palette.text.secondary
        },
        grid: {
            horzLines: {
                color: '#88888815',
                visible: false
            },
            vertLines: {
                color: '#88888815',
                visible: false
            }
        },
        rightPriceScale: {
            alignLabels: true,
            borderColor: theme.palette.divider,
            visible: isEmbedded ? false : showPnLScale,
            autoScale: true,
            invertScale: false,
            borderVisible: false
        },
        leftPriceScale: {
            alignLabels: true,
            borderColor: theme.palette.divider,
            borderVisible: false,
            visible: isEmbedded ? false : showOiScale,
            autoScale: false,
            invertScale: false,
            scaleMargins: {
                top: 0.75,
                bottom: 0
            }
        }
    }
}

export const getDefaultDisplayOptions = (): PayoffChartDisplayOptions => {
    return {
        showOI: true,
        showSdLines: true
    }
}